import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ArrowUpRight } from "lucide-react";
import { addDays, format, getISOWeek, parseISO, startOfISOWeek } from "date-fns";

import { CardShell } from "./CardShell";
import { Skeleton } from "../../../components/feedback/Skeleton";
import { useAvailability14d } from "../../../hooks/useAvailability14d";

const LOW_STOCK_THRESHOLD = 0.3;

function computeAlerts(availability, products) {
  if (!availability || !products?.length) return [];
  const productById = new Map(products.map((p) => [p.id, p]));
  const alerts = [];
  for (const row of availability.products || []) {
    const meta = productById.get(row.product_id);
    if (!meta) continue;
    const total = meta.in_stock || row.total_stock || 0;
    if (total <= 0) continue;
    const threshold = total * LOW_STOCK_THRESHOLD;
    // Find the first day that dips below the threshold.
    let worst = null;
    for (const d of row.daily || []) {
      if (d.available < threshold) {
        if (!worst || d.available < worst.available) worst = d;
      }
    }
    if (!worst) continue;
    alerts.push({
      product_id: row.product_id,
      product_name: meta.name,
      available: worst.available,
      total,
      date: worst.date,
      week: getISOWeek(parseISO(worst.date)),
    });
  }
  // Sort by severity (lowest available first).
  alerts.sort((a, b) => a.available - b.available);
  return alerts.slice(0, 4);
}

export function AvailabilityAlertsWidget({ products }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, data, error } = useAvailability14d();

  const alerts = useMemo(() => computeAlerts(data, products), [data, products]);

  const openAlert = (a) => {
    const monday = format(startOfISOWeek(parseISO(a.date)), "yyyy-MM-dd");
    const until = format(addDays(parseISO(monday), 13), "yyyy-MM-dd");
    navigate(
      `/availability?view=detail&product_id=${encodeURIComponent(a.product_id)}&from=${monday}&until=${until}`
    );
  };

  return (
    <CardShell
      title={t("dashboard.widgets.availability_alerts")}
      icon={AlertTriangle}
      testId="widget-availability-alerts"
    >
      {loading ? (
        <div className="space-y-2" data-testid="widget-availability-alerts-loading">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-4/5" />
        </div>
      ) : error ? (
        <p className="text-xs text-status-unavailable">{t("common.error_generic")}</p>
      ) : alerts.length === 0 ? (
        <div
          className="flex items-start gap-2 rounded-md bg-status-available/5 p-3"
          data-testid="widget-availability-alerts-empty"
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-status-available" />
          <p className="text-xs text-text-secondary">
            {t("dashboard.widgets.availability_alerts_empty")}
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="widget-availability-alerts-list">
          {alerts.map((a) => (
            <li key={a.product_id}>
              <button
                type="button"
                onClick={() => openAlert(a)}
                data-testid={`widget-availability-alert-${a.product_id}`}
                className="group flex w-full items-center gap-3 rounded-md border border-status-limited/25 bg-status-limited/5 p-2 text-left transition-colors hover:border-status-limited/50 hover:bg-status-limited/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-text-primary">
                    {a.product_name}
                  </div>
                  <div className="text-[11px] text-text-secondary">
                    {t("dashboard.widgets.availability_alerts_item", {
                      count: a.available,
                      week: a.week,
                    })}
                  </div>
                </div>
                <ArrowUpRight className="h-3.5 w-3.5 text-text-secondary transition-colors group-hover:text-charcoal" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}
